/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../locales/en-US/descriptors';
import { isObjectOrArrayAndEmpty } from '../../shared/Utils/Utils';
import ConnectionError from '../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../shared/StatusWrappers/ErrorStatus';
import {
  ECartAt,
  IDetailComponentProps,
  IUtilityBillData
} from '../../DashboardKPIWidgets/IEnergyManagementProps';
import AllUtilitiesDetailComponent from './AllUtilitiesDetailComponent';
import { Card, Icon, Tooltip } from '@scuf/common';
import { ApiContext } from '../../../context/ApiContext';

interface IAllUtilitiesDetailContentProps {
  data: IUtilityBillData;
  onRefresh: () => Promise<void>;
  isPrevPeriodForAllMonths?: boolean;
}

const AllUtilitiesDetailContent = (props: IAllUtilitiesDetailContentProps) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, isPrevPeriodForAllMonths } = props;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  return (
    <div className="allUtility-performance-detail-container">
      <div>
        <AllUtilitiesDetailComponent
          data={data}
          isPrevPeriodForAllMonths={isPrevPeriodForAllMonths}
        />
      </div>
    </div>
  );
};

const AllUtilitiesDetailWithLoader = withLoadingStatus(
  withErrorStatus(AllUtilitiesDetailContent)
);

const AllUtilitiesDetailWidget = (props: IDetailComponentProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { dataConnector } = props;

  const apiContext = useContext(ApiContext);

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const { all, loading, error, status } = dataConnector.data;
  const electricitySavings = (all as unknown) as IUtilityBillData;
  const isDataAvailable = electricitySavings ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as IUtilityBillData);
  const [isPrevPeriodForAllMonths, setIsPrevPeriodForAllMonths] = useState<
    boolean
  >(true);

  const checkIfAllMonthsHasPreviousPeriod = (data: IUtilityBillData) => {
    return (
      data &&
      data.monthwiseAggregations &&
      data.monthwiseAggregations.every(
        (item: {
          previousPeriodCost: number | null;
          totalActualCost: number | null;
        }) =>
          item.previousPeriodCost !== 0 &&
          item.previousPeriodCost !== null &&
          item.totalActualCost !== 0 &&
          item.totalActualCost
      )
    );
  };

  const checkIfAllSitesHasPreviousPeriod = (data: IUtilityBillData) => {
    return (
      data &&
      data.breakdownBySite &&
      data.breakdownBySite.every(
        (item: {
          aggregations: {
            previousPeriodCost: number | null;
            totalActualCost: number | null;
          };
        }) =>
          item.aggregations.previousPeriodCost !== 0 &&
          item.aggregations.previousPeriodCost !== null &&
          item.aggregations.totalActualCost !== 0 &&
          item.aggregations.totalActualCost !== null
      )
    );
  };

  const checkIfAllSiteHasCompleteData = (data: IUtilityBillData) => {
    return (
      data &&
      data.breakdownBySite &&
      data.breakdownBySite.every(
        (item: { isCompleted: boolean }) => item.isCompleted
      )
    );
  };

  const checkIfAllMonthHasCompleteData = (data: IUtilityBillData) => {
    return (
      data &&
      data.monthwiseAggregations &&
      data.monthwiseAggregations.every(
        (item: { isCompleted: boolean }) => item.isCompleted
      )
    );
  };

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        if (apiContext.apiCallAt === ECartAt.portfolioLabel) {
          setIsPrevPeriodForAllMonths(
            checkIfAllMonthsHasPreviousPeriod(electricitySavings) &&
              checkIfAllSitesHasPreviousPeriod(electricitySavings) &&
              checkIfAllSiteHasCompleteData(electricitySavings) &&
              checkIfAllMonthHasCompleteData(electricitySavings)
          );
        }

        setData(electricitySavings);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  return (
    <>
      <Card className="emaas-performance-detail-card">
        <Card.Header>
          <span className="emaas-summary-card-header">
            {t(d.UTILITYBILLSAVINGS)}
          </span>
          <Tooltip
            element={
              <Icon
                root="common"
                name="badge-info"
                exactSize="0.875rem"
                className="emaas-summary-card-header-info-icon"
              />
            }
            event="hover"
            content={t(d.UTILITYVIEWDETAILSTOOLTIP)}
            position="right center"
            size="small"
            header={t(d.UTILITYBILLDETAILSTOOLTIPHEADER)}
          />
        </Card.Header>
        <Card.Content className="detail-summary-card">
          <AllUtilitiesDetailWithLoader
            loading={isLoading}
            error={errorInfo}
            statusCode={statusCode}
            onRefresh={onRefresh}
            data={data}
            loadingText={t(d.LOADING)}
            title={t(d.CONNECTIONERRORTITLE)}
            noDataMessage={t(d.NODATAAVAILABLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            isPrevPeriodForAllMonths={isPrevPeriodForAllMonths}
          />
        </Card.Content>
      </Card>
    </>
  );
};
export default AllUtilitiesDetailWidget;
